import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { routesMap } from "@/components/routes/routes";
import OpticksCheckbox from "@/components/ui/OpticksCheckbox/OpticksCheckbox";
import OpticksButton from "@/components/ui/OpticksButton/OpticksButton";
import OpticksInput from "@/components/ui/OpticksInput/OpticksInput";
import OpticksLink from "@/components/ui/OpticksLink/OpticksLink";
import OpticksLogo from "@/components/ui/OpticksLogos/OpticksLogo";
import Spinner from "@/components/ui/Spinner/Spinner";
import type { MenuItem } from "@/components/ui/dropdowns/OpticksDropdown.types";
import type { AppSumoSignupFormPayload } from "@/types/model/Auth";
import { Controller, useForm } from "react-hook-form";
import { doAppSumoSignup } from "@/services/service-layer/authService";
import { useLoaderStore } from "@/zustand/loaderStore";
import { getTimezone } from "@/utilities/utils";
import OpticksMessage from "@/components/ui/OpticksMessage/OpticksMessage";
import OpticksDropdownSingleOption from "@/components/ui/dropdowns/single-option/OpticksDropdownSingleOption";
import AppSumoLogo from "@/components/ui/OpticksLogos/AppSumoLogo";

const SUB_VERTICAL_OPTIONS = [
  { value: "agency", label: "Digital Agency" },
  { value: "mediabuyer", label: "Media Buyer" },
  { value: "adnetwork", label: "Adnetwork / Affiliate" },
  { value: "leadgen", label: "Lead Generation" },
  { value: "gaming", label: "Gaming" },
  { value: "finance", label: "Finance" },
  { value: "insurance", label: "Insurance" },
  { value: "legal", label: "Legal services" },
  { value: "crypto", label: "Crypto" },
  { value: "healthcare", label: "Healthcare" },
  { value: "learning", label: "Education & E-Learning" },
  { value: "travel", label: "Travel & Hospitality" },
  { value: "car", label: "Automotive" },
  { value: "b2b", label: "B2B SaaS" },
  { value: "services", label: "Professional Services" },
  { value: "retail", label: "Retail & Consumer Goods" },
  { value: "fashion", label: "Fasion, Beauty & Cosmetics" },
  { value: "ecommerce", label: "Ecommerce" },
  { value: "adult", label: "Dating & Adult" },
  { value: "other", label: "Other" },
];

const AppSumoSignup = () => {
  const [searchParams] = useSearchParams();
  const licenseKey = searchParams.get("licenseKey") || "";
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { loading: isLoading, setLoading } = useLoaderStore((state) => state);

  const doAppSumoSignupFunction = (
    payload: Omit<
      AppSumoSignupFormPayload,
      "licenseKey" | "context" | "timezone"
    > & {
      subVerticalOption?: MenuItem;
    }
  ) => {
    setLoading(true);
    setServerErrors({});

    const timezone = getTimezone();

    const subVertical = payload.subVerticalOption?.value as string;

    doAppSumoSignup({
      email: payload.email,
      firstName: payload.firstName,
      companyName: payload.companyName,
      password: payload.password,
      phone: payload.phone,
      subVertical,
      terms: payload.terms,
      timezone,
      licenseKey,
      context: {
        pageUri: window.location.href,
        pageName: "AppSumo Signup",
      },
    });
  };

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<
    Omit<AppSumoSignupFormPayload, "licenseKey" | "context" | "timezone"> & {
      subVerticalOption?: MenuItem;
    }
  >({
    mode: "onChange",
    defaultValues: {
      email: "",
      firstName: "",
      companyName: "",
      password: "",
      phone: "",
      subVertical: "",
      subVerticalOption: undefined,
      terms: false,
    },
  });

  if (!licenseKey) {
    return (
      <>
        <h2>
          Sign up to <OpticksLogo /> <AppSumoLogo />
        </h2>
        <OpticksMessage type="warning">
          No license key provided. Please use the link from AppSumo.
        </OpticksMessage>
      </>
    );
  }
  return (
    <>
      <h2>
        Sign up to <OpticksLogo /> <AppSumoLogo />
      </h2>
      <form
        className="auth-form"
        noValidate={true}
        onSubmit={handleSubmit(doAppSumoSignupFunction)}
      >
        <div className="fields">
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Required",
              validate: (value) => {
                if (!/\S+@\S+\.\S{2,}/.test(value)) {
                  return "Invalid";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Email"
                  type="email"
                  required
                  surface="white"
                  input={field}
                  className="sign-up-email"
                />
                {(errors.email || serverErrors.email) && (
                  <span className="form-invalid-message">
                    {errors.email?.message || serverErrors.email}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="firstName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="First name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                  className="sign-up-email"
                />
                {(errors.firstName || serverErrors.firstName) && (
                  <span className="form-invalid-message">
                    {errors.firstName?.message || serverErrors.firstName}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="phone"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true; // Optional field
                if (!/^[+]?[\d\s()-]{8,}$/.test(value)) {
                  return "Invalid phone number";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Phone (optional)"
                  type="text"
                  surface="white"
                  input={{ ...field, value: field.value || "" }}
                  className="sign-up-email"
                />
                {(errors.phone || serverErrors.phone) && (
                  <span className="form-invalid-message">
                    {errors.phone?.message || serverErrors.phone}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="companyName"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Company name"
                  type="text"
                  required
                  surface="white"
                  input={field}
                  className="sign-up-email"
                />
                {(errors.companyName || serverErrors.companyName) && (
                  <span className="form-invalid-message">
                    {errors.companyName?.message || serverErrors.companyName}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="subVerticalOption"
            control={control}
            rules={{ required: "Required" }}
            render={({ field }) => (
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    width: "100%",
                  }}
                >
                  <div className="subvertical-dropdown">
                    <OpticksDropdownSingleOption
                      menuItems={SUB_VERTICAL_OPTIONS}
                      placeholder="Business Sector"
                      optionSelected={field.value}
                      handleChosenOptionChange={(menuItem: MenuItem) => {
                        field.onChange(menuItem);
                      }}
                      surface="white"
                      width="100%"
                    />
                  </div>
                </div>
                {(errors.subVerticalOption || serverErrors.subVertical) && (
                  <span className="form-invalid-message">
                    {errors.subVerticalOption?.message ||
                      serverErrors.subVertical}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              required: "Required",
              minLength: {
                value: 6,
                message: "Password must be at least 6 characters",
              },
              maxLength: {
                value: 16,
                message: "Password must be no more than 16 characters",
              },
            }}
            render={({ field }) => (
              <div>
                <OpticksInput
                  placeholder="Password"
                  type="password"
                  required
                  surface="white"
                  input={field}
                  className="sign-up-email"
                />
                {(errors.password || serverErrors.password) && (
                  <span className="form-invalid-message">
                    {errors.password?.message || serverErrors.password}
                  </span>
                )}
              </div>
            )}
          />

          <Controller
            name="terms"
            control={control}
            rules={{
              validate: (value) => value === true || "Required",
            }}
            render={({ field }) => (
              <div>
                <OpticksCheckbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                >
                  <span>
                    I accept the{" "}
                    <OpticksLink
                      href="https://optickssecurity.com/resources/files/Opticks-Terms-and-Conditions-for-Self-Service-v3.pdf"
                      label="terms and conditions"
                    />
                  </span>
                </OpticksCheckbox>

                {(errors.terms || serverErrors.terms) && (
                  <span className="form-invalid-message">
                    {errors.terms?.message || serverErrors.terms}
                  </span>
                )}
              </div>
            )}
          />

          {serverErrors.licenseKey && (
            <div style={{ marginTop: "10px" }}>
              <span className="form-invalid-message">
                {serverErrors.licenseKey}
              </span>
            </div>
          )}
        </div>

        <div className="login-btn">
          {!isLoading ? (
            <OpticksButton
              type="submit"
              size="m-wide"
              disabled={!isValid || isSubmitting}
              label="Sign Up"
            />
          ) : (
            <Spinner size={40} />
          )}
        </div>

        <div className="login-account">
          Already have an account?{" "}
          <Link to={routesMap.auth.login.path}>Login</Link>
        </div>
      </form>
    </>
  );
};

export default AppSumoSignup;
